import React from "react";
import {
  SHIFT_DAY_OPTIONS,
  formatDaysList,
  formatMinutesLabel,
  formatShiftTimesSummary,
  getScheduleForDay,
  getShiftBarStyle,
  getWindowBarStyle,
  HOUR_TICKS,
  isFullDaySchedule,
  sortDayCodes,
} from "../../utils/shiftSchedule";

const ShiftScheduleDetail = ({ shift, onClose, onEdit }) => {
  if (!shift) return null;

  const daysSet = new Set(sortDayCodes(shift.days || []));
  const schedules =
    Array.isArray(shift.day_schedules) && shift.day_schedules.length
      ? shift.day_schedules
      : null;
  const allSameTimes =
    !schedules ||
    schedules.every(
      (s) =>
        s.start_time === schedules[0].start_time &&
        s.end_time === schedules[0].end_time
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto py-4 px-2 sm:items-center"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[min(95vh,calc(100dvh-2rem))] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{shift.name}</h3>
            <div className="flex flex-wrap gap-2 mt-1">
              <span
                className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                  shift.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {shift.is_active ? "Active" : "Inactive"}
              </span>
              {shift.is_special_shift && (
                <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-900">
                  Special shift
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(shift)}
                className="px-4 py-2 rounded bg-[#674936] text-white text-sm hover:bg-[#503220]"
              >
                Edit
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm"
            >
              Close
            </button>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-3 bg-gray-50 text-sm space-y-1 border-b border-gray-100">
          <p>
            <span className="font-semibold text-gray-700">Scheduled work time:</span>{" "}
            {formatShiftTimesSummary(shift)}
          </p>
          {allSameTimes && (
            <>
              <p>
                <span className="font-semibold text-gray-700">Late allowable:</span>{" "}
                {formatMinutesLabel(shift.late_allowed_minutes ?? 0)}
              </p>
              <p>
                <span className="font-semibold text-gray-700">Early leave allowable:</span>{" "}
                {formatMinutesLabel(shift.early_leave_allowed_minutes ?? 0)}
              </p>
              <p>
                <span className="font-semibold text-gray-700">Break duration:</span>{" "}
                {formatMinutesLabel(shift.break_duration_minutes ?? 0)}
              </p>
              <p>
                <span className="font-semibold text-gray-700">Min. work time:</span>{" "}
                {formatMinutesLabel(shift.min_work_minutes ?? 0)}
              </p>
              <p>
                <span className="font-semibold text-gray-700">Valid check-in/out period:</span>{" "}
                {shift.check_in_start}–{shift.check_in_end} / {shift.check_out_start}–
                {shift.check_out_end}
              </p>
            </>
          )}
          {!allSameTimes && schedules && (
            <div className="space-y-2 pt-1">
              {sortDayCodes(schedules.map((s) => s.day)).map((day) => {
                const s = schedules.find((x) => x.day === day);
                if (!s) return null;
                const label =
                  SHIFT_DAY_OPTIONS.find((d) => d.code === day)?.label || day;
                return (
                  <div key={day} className="text-xs sm:text-sm border-t border-gray-200 pt-2">
                    <p className="font-semibold text-gray-800">{label}</p>
                    <p>
                      {s.start_time}–{s.end_time}
                      {isFullDaySchedule(s.start_time, s.end_time) && " (full day)"}
                      {" · "}late {formatMinutesLabel(s.late_allowed_minutes ?? 0)}
                      {" · "}min work {formatMinutesLabel(s.min_work_minutes ?? 0)}
                    </p>
                    <p className="text-gray-600">
                      Check-in {s.check_in_start}–{s.check_in_end} / Check-out{" "}
                      {s.check_out_start}–{s.check_out_end}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
          <p>
            <span className="font-semibold text-gray-700">Days:</span>{" "}
            {formatDaysList(shift.days)}
          </p>
        </div>

        <div className="px-4 sm:px-6 pt-2 flex flex-wrap gap-4 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-4 h-2 bg-[#c0392b] rounded-sm" />
            Scheduled work
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-4 h-2 bg-[#5b9bd5] rounded-sm opacity-90" />
            Check-in window
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-4 h-2 bg-[#2e6da4] rounded-sm opacity-90" />
            Check-out window
          </span>
        </div>

        <div className="p-4 sm:p-6 pt-2">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Schedule — Weekly timeline
          </h4>

          <div className="border border-gray-300 rounded overflow-hidden bg-[#e8f0f8]">
            <div className="flex border-b border-gray-300 bg-[#dce8f4] text-[10px] sm:text-xs text-gray-600">
              <div className="w-14 sm:w-16 shrink-0" />
              <div className="flex-1 flex justify-between px-1 py-1">
                {HOUR_TICKS.map((h) => (
                  <span key={`top-${h}`} className="w-0 text-center" style={{ flex: 1 }}>
                    {String(h).padStart(2, "0")}
                  </span>
                ))}
              </div>
            </div>

            {SHIFT_DAY_OPTIONS.map(({ code, label }) => {
              const active = daysSet.has(code);
              const daySchedule = getScheduleForDay(shift, code);
              const scheduledBarStyle = getShiftBarStyle(
                daySchedule.start_time,
                daySchedule.end_time
              );
              const checkInBarStyle = getWindowBarStyle(
                daySchedule.check_in_start,
                daySchedule.check_in_end
              );
              const checkOutBarStyle = getWindowBarStyle(
                daySchedule.check_out_start,
                daySchedule.check_out_end
              );
              return (
                <div
                  key={code}
                  className="flex border-b border-gray-200 last:border-b-0 min-h-[32px] sm:min-h-[36px]"
                >
                  <div className="w-14 sm:w-16 shrink-0 flex items-center justify-end pr-2 text-[10px] sm:text-xs text-gray-700 bg-[#dce8f4] border-r border-gray-300">
                    {label}
                  </div>
                  <div className="flex-1 relative bg-[#e8f0f8]">
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage:
                          "repeating-linear-gradient(to right, rgba(0,0,0,0.06) 0, rgba(0,0,0,0.06) 1px, transparent 1px, transparent calc(100% / 12))",
                      }}
                    />
                    {active ? (
                      <>
                        <div
                          className="absolute top-[18%] h-2 sm:h-2.5 bg-[#5b9bd5] rounded-sm z-[5] opacity-90"
                          style={checkInBarStyle}
                          title={`Check-in: ${daySchedule.check_in_start}–${daySchedule.check_in_end}`}
                        />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 h-3 sm:h-4 bg-[#c0392b] rounded-sm z-10"
                          style={scheduledBarStyle}
                          title={`Scheduled: ${daySchedule.start_time}–${daySchedule.end_time}`}
                        />
                        <div
                          className="absolute bottom-[18%] h-2 sm:h-2.5 bg-[#2e6da4] rounded-sm z-[5] opacity-90"
                          style={checkOutBarStyle}
                          title={`Check-out: ${daySchedule.check_out_start}–${daySchedule.check_out_end}`}
                        />
                      </>
                    ) : (
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-400">
                        —
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="flex border-t border-gray-300 bg-[#dce8f4] text-[10px] sm:text-xs text-gray-600">
              <div className="w-14 sm:w-16 shrink-0" />
              <div className="flex-1 flex justify-between px-1 py-1">
                {HOUR_TICKS.map((h) => (
                  <span key={`bot-${h}`} className="w-0 text-center" style={{ flex: 1 }}>
                    {String(h).padStart(2, "0")}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftScheduleDetail;
